import { deflate } from 'pako';
import { DEFAULT_KROKI_BASE_URL, getKrokiBaseUrlFromEnv } from '@core/config/KrokiConfig';

export type KrokiOutputFormat = 'svg' | 'png';

export const DEFAULT_KROKI_FORMAT: KrokiOutputFormat = 'svg';

const DEFAULT_DIAGRAM_TYPE = 'plantuml';
let krokiBaseUrlOverride: string | null = null;
let krokiBaseUrlInitializedFromEnv = false;
const krokiBaseUrlListeners = new Set<(baseUrl: string) => void>();

/**
 * Normalize a diagram type so users can pass shorthand aliases like "PlantUML" or "c4plantuml".
 */
export function normalizeDiagramType(input?: string | null): string {
  if (!input) return DEFAULT_DIAGRAM_TYPE;
  const slug = input.toString().trim().toLowerCase();
  if (!slug) return DEFAULT_DIAGRAM_TYPE;
  return /^[a-z0-9-]+$/.test(slug) ? slug : DEFAULT_DIAGRAM_TYPE;
}

/**
 * Encode raw diagram text so it can be passed to Kroki's GET endpoints.
 * Kroki expects deflated UTF-8 content encoded as base64url.
 */
export function encodeKrokiPayload(source: string): string {
  const normalized = normalizeLineEndings(source);
  const bytes = toUtf8Bytes(normalized);
  const compressed = deflate(bytes, { level: 9 });
  const base64 = toBase64(compressed);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/**
 * Build a ready-to-use Kroki URL for the requested diagram type and format.
 */
export function buildKrokiUrl(
  diagramType: string,
  source: string,
  format: KrokiOutputFormat = DEFAULT_KROKI_FORMAT,
  baseUrl?: string
): string {
  const sanitizedType = normalizeDiagramType(diagramType);
  const trimmedSource = source?.toString() ?? '';
  if (!trimmedSource.trim()) return '';
  const encoded = encodeKrokiPayload(trimmedSource);
  const safeBase = resolveKrokiBaseUrl(baseUrl);
  if (!safeBase) return '';
  return `${safeBase}/${sanitizedType}/${format}/${encoded}`;
}


/**
 * Override the Kroki base URL at runtime (e.g., after fetching /api/config).
 */
export function setKrokiBaseUrl(baseUrl?: string | null): void {
  krokiBaseUrlOverride = sanitizeBaseUrl(baseUrl);
  const resolved = getKrokiBaseUrl();
  krokiBaseUrlListeners.forEach((listener) => listener(resolved));
}

/**
 * Subscribe to runtime Kroki base URL changes.
 */
export function subscribeToKrokiBaseUrl(listener: (baseUrl: string) => void): () => void {
  krokiBaseUrlListeners.add(listener);
  return () => krokiBaseUrlListeners.delete(listener);
}

/**
 * Return the current Kroki base URL, applying runtime overrides or env defaults.
 */
export function getKrokiBaseUrl(): string {
  ensureServerEnvBaseLoaded();
  return krokiBaseUrlOverride ?? DEFAULT_KROKI_BASE_URL;
}

function resolveKrokiBaseUrl(baseUrl?: string): string {
  const sanitized = sanitizeBaseUrl(baseUrl ?? krokiBaseUrlOverride);
  if (sanitized) return sanitized;
  ensureServerEnvBaseLoaded();
  return DEFAULT_KROKI_BASE_URL;
}

function normalizeLineEndings(value: string): string {
  return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function toUtf8Bytes(value: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value);
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value, 'utf-8');
  }
  throw new Error('TextEncoder is not available in this environment.');
}

function toBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('base64');
  }

  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  if (typeof btoa === 'function') {
    return btoa(binary);
  }

  const globalBuffer = (globalThis as any)?.Buffer;
  if (globalBuffer) {
    return globalBuffer.from(bytes).toString('base64');
  }

  throw new Error('Base64 encoding is not supported in this environment.');
}

function sanitizeBaseUrl(value?: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.replace(/\/+$/, '');
}

function ensureServerEnvBaseLoaded(): void {
  if (typeof window !== 'undefined') return;
  if (krokiBaseUrlInitializedFromEnv) return;
  krokiBaseUrlInitializedFromEnv = true;
  try {
    const envBase = getKrokiBaseUrlFromEnv();
    krokiBaseUrlOverride = sanitizeBaseUrl(envBase);
  } catch {
    // Ignore env resolution failures; fallback to default.
  }
}
